import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import { styled } from '@mui/material/styles';
import { Card, Container, Stack, InputAdornment, Typography, Link as MuiLink } from '@mui/material';
import { LoadingButton } from '@mui/lab';

import { CustomTextBox } from 'components/forms';
import Person from '@mui/icons-material/Person';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { authReset, login } from 'redux/actions';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useToastMessage } from 'hooks';
import { RootReducerState } from 'utils/types';
import { Link, useNavigate } from 'react-router-dom';
import { HoverLoader } from 'components';
import { Helmet } from 'react-helmet-async';

const ContentStyle = styled('div')(({ theme }) => ({
  maxWidth: 480,
  margin: 'auto',
  minHeight: '100vh',
  display: 'flex',
  justifyContent: 'center',
  flexDirection: 'column',
  // padding: theme.spacing(12, 0),
}));

export default function Login() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { toastMessage } = useToastMessage();
  const [loading, setLoading] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const { handleSubmit, getValues, formState: { errors }, control } = useForm({ mode: 'all' });
  const { authData } = useSelector(
    ({ auth }: RootReducerState) => ({
      authData: auth.data
    }),
    shallowEqual
  );

  useEffect(() => {
    if (authData?.accessToken) {
      navigate('/admin/dashboard');
    } else if (loading) {
      dispatch(authReset());
    }
  }, [authData])

  async function onSubmit(data: any) {
    setLoading(true);
    dispatch(login(data, toastMessage, navigate));
    setTimeout(() => {
      setLoading(false)
    }, 1000);
  }

  return (
    <Container maxWidth="xl" sx={{ background: "#F5F5F5" }}>
      <Helmet>
        <title>Manage Your Property Listings - Gharawas Agent Login</title>
        <meta name="description" content="Log in to your agent account on Gharawas, the leading real estate platform. Access your listings, manage properties, and connect with potential buyers and sellers." />
        <meta name="keywords" content="Gharawas, real estate, agent login, property management, property listings, buyer-seller connections" />
        <meta name="robots" content="index, follow" />
        <meta name="author" content="Gharawas" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      </Helmet>
      {loading && <HoverLoader />}
      <ContentStyle>
        <Card sx={{ p: 2 }}>
          <Typography variant="h4" gutterBottom>
            Sign in
          </Typography>

          <Typography variant="body2" sx={{ mb: 2, display: 'inline' }}>
            Don’t have an account? {''}
            <Link to='/auth/register' style={{ textDecoration: 'none', color: "#6327e4", fontSize: 14 }}>
              Register Now
            </Link>
          </Typography>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Stack spacing={2}>
              <CustomTextBox
                label="Email"
                error={errors.email}
                name="email"
                rules={{ required: true }}
                control={control}
                margin="normal"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end" >
                      <span className="pe-auto"><Person color="primary" /></span>
                    </InputAdornment>
                  )
                }}
              />

              <CustomTextBox
                label="Password"
                type={showPassword ? 'text' : 'password'}
                error={errors.password}
                name="password"
                rules={{ required: true }}
                control={control}
                margin="normal"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <MuiLink href="#" underline="none" onClick={() => {
                        setShowPassword((prev) => !prev)
                      }}>
                        {!showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </MuiLink>
                    </InputAdornment>
                  )
                }}
              />
            </Stack>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ my: 1 }}>
              <div></div>
              <Link to="/auth/forgot-password" style={{ textDecoration: 'none' }}>
                <Typography variant='body2' color="gray">
                  Forgot password?
                </Typography>
              </Link>
            </Stack>
            <LoadingButton fullWidth size="large" type="submit" variant="contained" sx={{ my: 2 }}>
              Login
            </LoadingButton>
          </form>
        </Card>
      </ContentStyle>
    </Container>
  );
}
